import Image from "next/image";
import Link from "next/link";
import { Gamja_Flower } from "next/font/google";
import { HelpCircle } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const gamjaFlower = Gamja_Flower({
  weight: "400",
  subsets: ["latin"],
  display: "swap",
  fallback: ["serif"],
});

// 과목 데이터는 모듈 상단의 가벼운 배열로 정의(무거운 posts 데이터 import 금지 — 클라이언트 번들 비대화 방지).
type Subject = "theory" | "coding";

const THEORY_SUBJECTS = [
  {
    name: "DB",
    image: "/main/db_gamza.webp",
    href: "/theory/db",
  },
  {
    name: "네트워크/OS",
    image: "/main/network_gamza.webp",
    href: "/theory/network-os",
  },
  {
    name: "SW 개발",
    image: "/main/dev_gamza.webp",
    href: "/theory/sw-dev",
  },
  {
    name: "SW 설계",
    image: "/main/design_gamza.webp",
    href: "/theory/sw-design",
  },
  {
    name: "보안/신기술",
    image: "/main/security_gamza.webp",
    href: "/theory/security-newtech",
  },
];

const CODING_SUBJECTS = [
  {
    name: "C언어",
    image: "/coding/c-language/c-language_gamja.webp",
    href: "/coding/c-language",
  },
  {
    name: "Java",
    image: "/coding/java/java_gamja.webp",
    href: "/coding/java",
  },
  {
    name: "Python",
    image: "/coding/python/python_gamja.webp",
    href: "/coding/python",
  },
  {
    name: "SQL",
    image: "/coding/sql/sql_gamja.webp",
    href: "/coding/sql",
  },
  {
    name: "공통 기초",
    image: "/coding/common/common_gamja.webp",
    href: "/coding/common",
  },
];

const SUBJECT_DATA: Record<
  Subject,
  { categories: typeof THEORY_SUBJECTS; helpText: string }
> = {
  theory: {
    categories: THEORY_SUBJECTS,
    helpText: "정보처리기사 실기 이론 공략을 위해 감자가 직접 나눈 과목이에요",
  },
  coding: {
    categories: CODING_SUBJECTS,
    helpText: "정보처리기사 실기 코딩 공략을 위해 감자가 직접 나눈 분류예요",
  },
};

const CategoryButton = ({
  name,
  image,
  href,
}: {
  name: string;
  image: string;
  href: string;
}) => (
  <Link
    href={href}
    prefetch={false}
    className="flex items-center rounded-lg border border-border bg-card p-3 shadow-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
  >
    <Image
      src={image}
      alt={name}
      width={40}
      height={40}
      className="mr-3 h-10 w-10 shrink-0 object-contain"
    />
    <span className="font-semibold text-sm">{name}</span>
  </Link>
);

const CategoryHelpPopover = ({ helpText }: { helpText: string }) => (
  <Popover>
    <PopoverTrigger
      aria-label="과목 분류 안내"
      className="grid h-7 w-7 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
    >
      <HelpCircle aria-hidden="true" className="w-5 h-5" />
    </PopoverTrigger>
    <PopoverContent>
      <p className="text-sm">{helpText}</p>
    </PopoverContent>
  </Popover>
);

const CategoryButtons = ({
  variant = "default",
  subject = "theory",
}: {
  /** "default": 인덱스 본문 그리드 / "sidebar": IndexShell 좌측 사이드바 세로 스택 (JCG-2097) */
  variant?: "default" | "sidebar";
  /** "theory": 이론 과목 / "coding": 코딩 분류 (JCG-2099). variant 와 직교. 기본 "theory". */
  subject?: Subject;
}) => {
  const { categories, helpText } = SUBJECT_DATA[subject];

  // 사이드바 변형 — IndexShell 좌측 슬롯용 세로 스택(과목 5개를 위→아래로).
  if (variant === "sidebar") {
    return (
      <section className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <h2 className={`text-lg font-semibold ${gamjaFlower.className}`}>
            과목별로 찾아보기
          </h2>
          <CategoryHelpPopover helpText={helpText} />
        </div>
        <div className="flex flex-col gap-2">
          {categories.map((category) => (
            <CategoryButton key={category.name} {...category} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="my-8">
      <div className="flex justify-center items-center gap-2 mt-4 mb-6">
        <h2
          className={`text-center text-xl font-semibold ${gamjaFlower.className}`}
        >
          과목별로 찾아보기
        </h2>
        <CategoryHelpPopover helpText={helpText} />
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden px-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {categories.slice(0, 2).map((cat) => (
            <CategoryButton key={cat.name} {...cat} />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-4">
          {categories.slice(2, 5).map((cat) => (
            <CategoryButton key={cat.name} {...cat} />
          ))}
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:grid md:grid-cols-5 gap-6 max-w-7xl mx-auto px-4">
        {categories.map((category) => (
          <CategoryButton key={category.name} {...category} />
        ))}
      </div>
    </section>
  );
};

export default CategoryButtons;
